import { FC, PropsWithChildren, useEffect, useState } from "react";
import { createPortal } from "react-dom";

export const NUMBER_OF_RENDERS_TO_CHECK_FOR_CONTAINER = 3;

/**
 * Utility HOC for creating a portal at the specified element ID. "Deferred" as
 * the component will search for the element with the given ID for a few render
 * cycles in case it doesn't yet exist.
 * @param portalElementId - Element ID for portal.
 * @returns React portal component.
 */
export function createDeferredPortal(portalElementId: string) {
  const DeferredPortal: FC<PropsWithChildren> = ({ children }) => {
    const [renderCount, setRenderCount] = useState(0);
    const container = document.getElementById(portalElementId);

    useEffect(() => {
      if (container) return;

      if (renderCount < NUMBER_OF_RENDERS_TO_CHECK_FOR_CONTAINER) {
        // if portal container isn't found on first render then enforce another
        setRenderCount(renderCount + 1);
      } else {
        throw new Error(
          `Could not create portal as element [id="${portalElementId}"] could not be found.`
        );
      }
    }, [renderCount]);

    if (!container) return null;

    return createPortal(children, container);
  };

  return DeferredPortal;
}
